import React, { useMemo } from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";
import { evaluateExpression } from "../../engine/evaluateBindings";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function evalExpr(value, params, fallback = 0) {
  return evaluateExpression(value, params, fallback);
}

function getInfillColor(type) {
  const t = String(type || "").toLowerCase();

  if (t === "glass") return "#93c5fd";
  if (t === "mirror") return "#cbd5e1";
  if (t === "board") return "#d6b98c";
  if (t === "metal") return "#9ca3af";

  return "#bae6fd";
}

function RailBox({
  name,
  position,
  args,
  selected,
  color = "#64748b",
}) {
  return (
    <mesh position={position} castShadow receiveShadow userData={{ part: name }}>
      <boxGeometry args={args} />
      <meshStandardMaterial color={color} roughness={0.45} metalness={0.15} />
      {selected ? <Edges scale={1.002} color="#1d4ed8" /> : null}
    </mesh>
  );
}

export default function FramedPanelPreview3D({
  profile,
  parameters = {},
  selected = false,
}) {
  const safeProfile = profile || {};
  const frame = safeProfile.frame || {};
  const infill = safeProfile.infill || {};
  const outerProfile = safeProfile.outerProfile || {};

  const dims = useMemo(() => {
    const width = evalExpr(outerProfile.width_expr ?? "width", parameters, 600);
    const height = evalExpr(outerProfile.height_expr ?? "height", parameters, 720);

    const frameWidth = Math.max(
      0,
      evalExpr(frame.frameWidth ?? "frameWidth", parameters, 70)
    );

    const frameThickness = Math.max(
      0.1,
      evalExpr(frame.thickness ?? "frameThickness", parameters, 22)
    );

    const infillInset = Math.max(
      0,
      evalExpr(infill.inset ?? "frameWidth", parameters, frameWidth)
    );

    const infillThickness = Math.max(
      0.1,
      evalExpr(infill.thickness ?? "infillThickness", parameters, 4)
    );

    const innerWidth = Math.max(1, width - frameWidth * 2);
    const innerHeight = Math.max(1, height - frameWidth * 2);

    const infillWidth = Math.max(1, width - infillInset * 2);
    const infillHeight = Math.max(1, height - infillInset * 2);

    return {
      width,
      height,
      frameWidth,
      frameThickness,
      infillInset,
      infillThickness,
      innerWidth,
      innerHeight,
      infillWidth,
      infillHeight,
    };
  }, [outerProfile, frame, infill, parameters]);

  const {
    width,
    height,
    frameWidth,
    frameThickness,
    infillThickness,
    innerWidth,
    innerHeight,
    infillWidth,
    infillHeight,
  } = dims;

  const frameEnabled = frame.enabled !== false && frameWidth > 0;
  const infillEnabled = infill.enabled !== false;

  const zFrame = 0;
  const zInfill = -frameThickness / 2 + infillThickness / 2;

  const frameColor = "#6b7280";
  const infillColor = getInfillColor(infill.type);

  return (
    <group>
      {infillEnabled && (
        <mesh position={[0, 0, zInfill]} castShadow receiveShadow>
          <boxGeometry args={[infillWidth, infillHeight, infillThickness]} />
          <meshPhysicalMaterial
            color={infillColor}
            roughness={String(infill.type).toLowerCase() === "glass" ? 0.05 : 0.25}
            metalness={String(infill.type).toLowerCase() === "mirror" ? 0.5 : 0}
            transparent={["glass", "mirror"].includes(String(infill.type).toLowerCase())}
            opacity={String(infill.type).toLowerCase() === "glass" ? 0.38 : 0.85}
            transmission={String(infill.type).toLowerCase() === "glass" ? 0.35 : 0}
            side={THREE.DoubleSide}
          />
          {selected ? <Edges scale={1.003} color="#0ea5e9" /> : null}
        </mesh>
      )}

      {frameEnabled && (
        <>
          <RailBox
            name="left_rail"
            selected={selected}
            color={frameColor}
            position={[-width / 2 + frameWidth / 2, 0, zFrame]}
            args={[frameWidth, height, frameThickness]}
          />

          <RailBox
            name="right_rail"
            selected={selected}
            color={frameColor}
            position={[width / 2 - frameWidth / 2, 0, zFrame]}
            args={[frameWidth, height, frameThickness]}
          />

          <RailBox
            name="top_rail"
            selected={selected}
            color={frameColor}
            position={[0, height / 2 - frameWidth / 2, zFrame]}
            args={[innerWidth, frameWidth, frameThickness]}
          />

          <RailBox
            name="bottom_rail"
            selected={selected}
            color={frameColor}
            position={[0, -height / 2 + frameWidth / 2, zFrame]}
            args={[innerWidth, frameWidth, frameThickness]}
          />
        </>
      )}

      <group position={[0, 0, frameThickness / 2 + 2]}>
        <mesh position={[0, height / 2, 0]}>
          <boxGeometry args={[width, 4, 4]} />
          <meshBasicMaterial color="#f59e0b" />
        </mesh>

        <mesh position={[0, -height / 2, 0]}>
          <boxGeometry args={[width, 4, 4]} />
          <meshBasicMaterial color="#a855f7" />
        </mesh>

        <mesh position={[-width / 2, 0, 0]}>
          <boxGeometry args={[4, height, 4]} />
          <meshBasicMaterial color="#ef4444" />
        </mesh>

        <mesh position={[width / 2, 0, 0]}>
          <boxGeometry args={[4, height, 4]} />
          <meshBasicMaterial color="#22c55e" />
        </mesh>
      </group>
    </group>
  );
}